import { useAccount } from "../account/useAccount";
import { useMembers } from "../member/useMembers";
import { useTeams } from "../member/useTeams";
import { getMemberIdsFromShares } from "./getMemberIdsFromShares";
import { getTeamIdsFromShares } from "./getTeamIdsFromShares";
import { FolderShare, useQueryFolderShares } from "./useQueryFolderShares";

interface Input {
  folderId?: string;
  rootId?: string | null;
}

export interface HydratedFolderShare extends FolderShare {
  member?: ReturnType<typeof useMembers>["members"][number];
  team?: ReturnType<typeof useTeams>["teams"][string];
  account?: ReturnType<typeof useAccount>["account"];
}

export function useFolderSharing({ folderId, rootId }: Input) {
  const { shares, isSharesLoading } = useQueryFolderShares({
    folderId: rootId || folderId,
  });

  // Fetch account data in case this folder is shared with "everyone"
  const { account, isLoading: isLoadingAccount } = useAccount();

  // Fetch all members that have individual access
  const memberIds = getMemberIdsFromShares(shares);
  const { members, isLoading: isLoadingMembers } = useMembers({
    ids: memberIds,
  });

  // Fetch all teams that have access
  const teamIds = getTeamIdsFromShares(shares);
  const { teams, isLoading: isLoadingTeams } = useTeams({
    ids: teamIds,
  });

  // Hydrate the share objects with members, teams, and account
  const hydratedShares = shares.map((share) => {
    const member = members[share.shareId];
    const team = teams[share.shareId];
    const accumulator =
      share.shareType === "account" || share.shareType === "accountadmin"
        ? account
        : undefined;

    return {
      ...share,
      ...(member ? { member } : {}),
      ...(team ? { team } : {}),
      ...(accumulator ? { account: accumulator } : {}),
    };
  });

  return {
    shares: hydratedShares,
    isSharesLoading,
    isHydrating: isLoadingMembers || isLoadingTeams || isLoadingAccount,
  };
}
